import { useOrderContext } from "app/providers/OrderProvider";
import { OrderPayment } from "shared/api/middleware-api-client";
import { idealId, sprayPayId, statusDenied } from "shared/utils/payment";
import Heading from "shared/ui/typography/Heading";
import Text from "shared/ui/typography/Text";
import StatusSupport from "widgets/status-support";
import { twMerge } from "tailwind-merge";
import { isDevelopmentEnvironment } from "shared/utils/checkEnvironment";
import Button, { ButtonArrowTypes, ButtonTypes } from "shared/ui/button";
import StatusGif from "entities/status-gif";

interface FailureProps {
  payment: OrderPayment | undefined;
  orderNumber: string;
}

const Failure = ({ payment, orderNumber }: FailureProps) => {
  const orderContext = useOrderContext();
  const { orderStatusId, orderReference } = orderContext;

  const onTryPaymentAgainClick = () => {
    const urlParam = payment?.orderType === "Order" ? "orderId" : "pOrderId";

    window.location.href = `/betalen/?${urlParam}=${orderReference}`;
  };

  const PageContent = () => {
    if (!payment) return <></>;

    switch (payment.transaction?.method) {
      case sprayPayId:
        if (orderStatusId === statusDenied) {
          return (
            <>
              <Heading headingLevel="h1" className="gg-mb-5 md:!gg-text-5xl">
                Je betaling via SprayPay is niet gelukt
              </Heading>

              <Text className="gg-text-lg">
                Helaas heeft SprayPay jouw aanvraag voor betalen in termijnen
                afgekeurd. Neem contact met onze klantenservice op. Dan kijken
                we samen naar de mogelijkheden.
              </Text>
            </>
          );
        } else {
          return (
            <>
              <Heading headingLevel="h1" className="gg-mb-5 md:!gg-text-5xl">
                Betaling met SprayPay mislukt
              </Heading>

              <Text className="gg-text-lg">
                Je hebt een aanvraag gedaan voor het betalen in termijnen via
                SprayPay en deze aanvraag is mislukt.
              </Text>
            </>
          );
        }
      default:
        return (
          <>
            <Heading headingLevel="h1" className="gg-mb-10 md:!gg-text-5xl">
              Oh nee…
            </Heading>

            <Text className="gg-text-lg gg-mb-5">
              {`Er ging iets mis tijdens het betalen van je order ${orderNumber}. 
                Misschien ligt het aan ons, dat kan. Je kunt het nogmaals proberen met de betaalknop hieronder. Of neem contact met ons op.`}
            </Text>

            <Text className="gg-text-lg gg-mb-3">
              Je wordt naar onze betaalomgeving gebracht om het nogmaals te
              proberen.{" "}
            </Text>

            <Button
              type={ButtonTypes.YELLOW}
              arrow={ButtonArrowTypes.RIGHT}
              onClick={() => onTryPaymentAgainClick()}
              className="gg-font-bold gg-mb-20"
            >
              Opnieuw proberen
            </Button>

            <Text className="gg-mb-2">Neem contact met ons op</Text>
            <Text className="">
              {
                "Lukt het niet om de betaling te doen of heb je nog vragen over het betalen van je bestelling? Neem dan even contact met ons op via"
              }{" "}
              <a className={"gg-underline"} href="tel:010 307 2889">
                010 307 2889
              </a>{" "}
              {"of"}{" "}
              <a
                className={"gg-underline"}
                href="mailto:klantenservice@gewoongers.nl"
              >
                klantenservice@gewoongers.nl
              </a>
            </Text>
          </>
        );
    }
  };

  return (
    <div
      className={twMerge(
        isDevelopmentEnvironment() ? "gg-mx-auto" : "",
        "gg-max-w-3xl"
      )}
    >
      <div className="gg-mb-20">
        <StatusGif
          src={`https://media1.giphy.com/media/EyScIhuOPbvuxbHeIa/giphy.gif`}
          alt="Een GewoonGers Gif. Monteur shrugs."
        />

        {payment !== undefined && <PageContent />}
      </div>

      <div>
        <Heading headingLevel="h2" className="gg-mb-8 gg-text-3xl">
          Wat nu?
        </Heading>

        <StatusSupport showTryPaymentAgainCard />
      </div>
    </div>
  );
};
export default Failure;
